import { DateTime } from 'luxon';
import Chart from 'react-apexcharts';
import React, {
  FunctionComponent,
  useState,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Modal, Button, Table } from 'react-bootstrap';
import apis from '../../apis';
import {
  HeaderDisplayKeysWithType,
  InlineEditableTable,
} from '../../components';
import {
  PHIWH,
  Semester,
  Student,
  StudentPHIAll,
  YearSemClasses,
} from '../../model';
import { WHTableHeader } from '../TableHeaders/WHHeader';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import ExcelJS from 'exceljs';
import { downloadBlob } from '../../utils/element';
import ApexCharts from 'apexcharts';
import { ApexOptions } from 'apexcharts';
import { I18N } from '../../i18n-raw';

const inlineWHTableHeader: HeaderDisplayKeysWithType<PHIWH>[] = [
  { property: 'year', display: '學年' },
  { property: 'sem', display: '學期' },
  ...WHTableHeader,
];

const numberToChinese = (num: number) =>
  ['零', '一', '二', '三', '四', '五', '六'][num] ?? num.toString();

const bmiCodeMap: Record<string, string> = {
  '-2': '體型瘦弱',
  '-1': '體重過輕',
  '0': '體重適中',
  '1': '體重過重',
  '2': '體重超重',
};

type Props = {
  editable: boolean;
  whs: PHIWH[];
  student: StudentPHIAll;
  schoolId: string;
  semesters: Semester[];
  yearSems: YearSemClasses[];
  onApiError: (e: any) => void;
  onValueUpdated: () => void;
};

export const WHSection: FunctionComponent<Props> = ({
  editable,
  whs,
  student,
  semesters,
  yearSems,
  schoolId,
  onApiError,
  onValueUpdated,
}) => {
  const currentSemester = semesters.find((s) => s.isNow);
  const [inputing, setInputing] = useState(false);
  const [whsightHistoryChecked, whHistoryChecked] = useState<boolean>(false);
  const [whHistory, setWHHistory] = useState([] as PHIWH[]);
  const nowYearSem = yearSems.find(
    (s) => s.sem === currentSemester?.sem && s.year === currentSemester?.year
  );
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartReady, setChartReady] = useState(false);

  const wh = useMemo(
    () =>
      whs.find(
        (s) =>
          s.sem === currentSemester?.sem && s.year === currentSemester?.year
      ),
    [currentSemester, whs]
  );

  yearSems
    .filter((s) => !whs.some((wh) => wh.sem === s.sem && wh.year === s.year))
    .forEach((ys) => whs.push({ ...ys, id: 0 }));

  whs.sort((a, b) => a.sem - b.sem + (a.year - b.year) * 100);

  function onValueUpdate(value: PHIWH) {
    if (value.height == -9 || value.weight == -9) {
      value.examDate = null;
    } else if (value.weight != -9 && value.height != -9 && !value.examDate) {
      toast.error('請填入測量日');
      return;
    }
    apis
      .InsertStudentBodyMetrix(student.pid, value)
      .catch(onApiError)
      .then(onValueUpdated);
  }

  function onValueDelete(value: PHIWH) {
    apis
      .deleteStudentWH(student.pid, value.year, value.sem)
      .catch(onApiError)
      .then(onValueUpdated);
  }

  function getWHHistory() {
    if (!whHistory.length && currentSemester?.year && nowYearSem) {
      apis
        .GetBodyMatrixHistory(
          student.pid,
          currentSemester?.year,
          nowYearSem.grade
        )
        .then((student) => {
          setWHHistory(student);
        })
        .catch(onApiError);
    }
  }

  function getImageSizeFromDataURL(
    dataURL: string
  ): Promise<{ width: number; height: number }> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.src = dataURL;
    });
  }

  async function handleExportExcelWithChart() {

    const sortedHistory = [...whs].sort(
      (a, b) =>
        (a.classGrade ?? 0) * 10 + a.sem - ((b.classGrade ?? 0) * 10 + b.sem)
    );

    const imageData = await createGrowthChartImage(sortedHistory);

    if (!imageData) {
      toast.error('圖表尚未載入完成，請稍候再試');
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('生長發育');
    sheet.mergeCells('A1:F1');
    sheet.getCell(
      'A1'
    ).value = `${nowYearSem?.grade}年${nowYearSem?.no}班${student.seat}號${student.name}生長發育歷程`;
    sheet.getCell('A1').alignment = { horizontal: 'center' };
    sheet.getCell('A1').font = { bold: true, size: 14 };

    sheet.getColumn(1).width = 8;
    sheet.getColumn(2).width = 8;
    sheet.getColumn(3).width = 10;
    sheet.getColumn(4).width = 10;
    sheet.getColumn(5).width = 10;
    sheet.getColumn(6).width = 15;

    sheet.addRow(['年級', '學期', '身高', '體重', 'BMI', '判讀']);

    sortedHistory.forEach((item) => {
      sheet.addRow([
        numberToChinese(item.classGrade ?? 0),
        item.sem,
        item.height,
        item.weight,
        item.bmi?.toFixed(2),
        bmiCodeMap[item.bmiCode?.toString() ?? ''] ?? '未測量',
      ]);
    });

    const imgSize = await getImageSizeFromDataURL(imageData);
    const displayWidth = 500;
    const scale = displayWidth / imgSize.width;
    const displayHeight = imgSize.height * scale;

    const imageId = workbook.addImage({ base64: imageData, extension: 'png' });
    sheet.addImage(imageId, {
      tl: { col: 8, row: 1 },
      ext: { width: displayWidth, height: displayHeight },
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    downloadBlob(
      blob,
      `${nowYearSem?.grade}年${nowYearSem?.no}班${student.seat}號${student.name}生長發育歷程.xlsx`
    );
  }

  async function createGrowthChartImage(data: PHIWH[]): Promise<string> {

    const filteredData = data.filter(
      (d) =>
        d.height !== null &&
        d.height !== undefined &&
        d.weight !== null &&
        d.weight !== undefined
    );

    if (filteredData.length === 0) {
      return '';
    }
    const width = 700;
    const height = 500;

    const padding = {
      top: 80,
      right: 90,
      bottom: 90,
      left: 90,
    };

    const chartWidth = width - padding.left - padding.right;
    const chartHeight = height - padding.top - padding.bottom;

    const labels = filteredData.map(
      (d) => `${numberToChinese(d.classGrade ?? 0)}-${d.sem}`
    );

    const heights = filteredData.map((d) => Number(d.height));
    const weights = filteredData.map((d) => Number(d.weight));

    const hMin = Math.floor(Math.min(...heights) - 1);
    const hMax = Math.ceil(Math.max(...heights) + 1);

    const wMin = Math.floor(Math.min(...weights) - 1);
    const wMax = Math.ceil(Math.max(...weights) + 1);

    const getX = (index: number) =>
      filteredData.length === 1
        ? padding.left + chartWidth / 2
        : padding.left + (chartWidth / (filteredData.length - 1)) * index;

    const getHeightY = (value: number) =>
      padding.top +
      chartHeight -
      ((value - hMin) / (hMax - hMin)) * chartHeight;

    const getWeightY = (value: number) =>
      padding.top +
      chartHeight -
      ((value - wMin) / (wMax - wMin)) * chartHeight;

    const heightPoints = heights.map((h, i) => ({
      x: getX(i),
      y: getHeightY(h),
    }));

    const weightPoints = weights.map((w, i) => ({
      x: getX(i),
      y: getWeightY(w),
    }));

    const createLine = (points: { x: number; y: number }[]) =>
      points.map((p) => `${p.x},${p.y}`).join(' ');

    const createCircles = (
      points: { x: number; y: number }[],
      color: string
    ) =>
      points
        .map(
          (p) =>
            `<circle cx="${p.x}" cy="${p.y}" r="5" fill="${color}" stroke="white" stroke-width="1"/>`
        )
        .join('');

    const tickCount = 5;

    const yTicks = Array.from({ length: tickCount }, (_, i) => {
      const ratio = i / (tickCount - 1);

      const hValue = hMax - (hMax - hMin) * ratio;
      const wValue = wMax - (wMax - wMin) * ratio;

      const y = padding.top + chartHeight * ratio;

      return `
      <line
        x1="${padding.left}"
        y1="${y}"
        x2="${padding.left + chartWidth}"
        y2="${y}"
        stroke="#ddd"
      />

      <text
        x="${padding.left - 10}"
        y="${y + 5}"
        text-anchor="end"
        font-size="12"
      >
        ${hValue.toFixed(1)}
      </text>

      <text
        x="${padding.left + chartWidth + 10}"
        y="${y + 5}"
        text-anchor="start"
        font-size="12"
      >
        ${wValue.toFixed(1)}
      </text>
    `;
    }).join('');

    const xLabels = labels
      .map((label, i) => {
        const x = getX(i);

        return `
        <text
          x="${x}"
          y="${height - 60}"
          text-anchor="end"
          font-size="12"
          transform="rotate(-50 ${x},${height - 50})"
        >
          ${label}
        </text>
      `;
      })
      .join('');

    const svg = `
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="${width}"
    height="${height}"
  >
    <rect width="100%" height="100%" fill="white"/>

    <text
      x="${width / 2}"
      y="40"
      text-anchor="middle"
      font-size="28"
      font-weight="bold"
    >
      生長發育圖
    </text>

    <circle cx="280" cy="65" r="7" fill="#2196f3"/>
    <text x="295" y="70" font-size="16">身高</text>

    <circle cx="370" cy="65" r="7" fill="#00c853"/>
    <text x="385" y="70" font-size="16">體重</text>

    <line
      x1="${padding.left}"
      y1="${padding.top}"
      x2="${padding.left}"
      y2="${padding.top + chartHeight}"
      stroke="#666"
    />

    <line
      x1="${padding.left + chartWidth}"
      y1="${padding.top}"
      x2="${padding.left + chartWidth}"
      y2="${padding.top + chartHeight}"
      stroke="#666"
    />

    <line
      x1="${padding.left}"
      y1="${padding.top + chartHeight}"
      x2="${padding.left + chartWidth}"
      y2="${padding.top + chartHeight}"
      stroke="#666"
    />

    ${yTicks}

    <polyline
      points="${createLine(heightPoints)}"
      fill="none"
      stroke="#2196f3"
      stroke-width="3"
    />

    <polyline
      points="${createLine(weightPoints)}"
      fill="none"
      stroke="#00c853"
      stroke-width="3"
    />

    ${createCircles(heightPoints, '#2196f3')}
    ${createCircles(weightPoints, '#00c853')}

    ${xLabels}

    <text
      x="30"
      y="${height / 2}"
      text-anchor="middle"
      font-size="16"
      transform="rotate(-90 30 ${height / 2})"
    >
      身高（公分）
    </text>

    <text
      x="${width - 25}"
      y="${height / 2}"
      text-anchor="middle"
      font-size="16"
      transform="rotate(90 ${width - 25} ${height / 2})"
    >
      體重（公斤）
    </text>

    <text
      x="${width / 2}"
      y="${height - 10}"
      text-anchor="middle"
      font-size="16"
    >
      學期年級
    </text>

  </svg>
  `;

    const svgBlob = new Blob([svg], {
      type: 'image/svg+xml;charset=utf-8',
    });

    const url = URL.createObjectURL(svgBlob);

    return await new Promise<string>((resolve) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d')!;

        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, width, height);

        ctx.drawImage(img, 0, 0);

        URL.revokeObjectURL(url);

        resolve(canvas.toDataURL('image/png'));
      };

      img.src = url;
    });
  }

  // function calculateAge(birthDate: DateTime): number {
  //   const birthDateObj = new Date(
  //     birthDate.year,
  //     birthDate.month - 1,
  //     birthDate.day
  //   );
  //   const today = new Date();
  //   let age = today.getFullYear() - birthDateObj.getFullYear();
  //   const monthDiff = today.getMonth() - birthDateObj.getMonth();

  //   if (
  //     monthDiff < 0 ||
  //     (monthDiff === 0 && today.getDate() < birthDateObj.getDate())
  //   ) {
  //     age--;
  //   }
  //   return age;
  // }

  function calculateAge(
    birthDate: DateTime,
    examDate?: DateTime | null
  ): number {
    if (!birthDate) return 0;

    // 👉 如果沒有測量日才 fallback today
    const baseDate = examDate
      ? new Date(examDate.year, examDate.month - 1, examDate.day)
      : new Date();

    const birthDateObj = new Date(
      birthDate.year,
      birthDate.month - 1,
      birthDate.day
    );

    let age = baseDate.getFullYear() - birthDateObj.getFullYear();
    const monthDiff = baseDate.getMonth() - birthDateObj.getMonth();

    if (
      monthDiff < 0 ||
      (monthDiff === 0 && baseDate.getDate() < birthDateObj.getDate())
    ) {
      age--;
    }

    return age < 0 ? 0 : age;
  }

  const chartHistory = useMemo(
    () =>
      [...whs]
        .filter(
          (s) =>
            s.classGrade !== undefined &&
            s.classGrade !== null &&
            s.height !== undefined &&
            s.height !== null &&
            s.weight !== undefined &&
            s.weight !== null &&
            s.height > 0 &&
            s.weight > 0
        )
        .sort(
          (a, b) =>
            (a.classGrade ?? 0) * 10 +
            (a.sem ?? 0) -
            ((b.classGrade ?? 0) * 10 + (b.sem ?? 0))
        ),
    [whs]
  );

  const heightSeries = chartHistory.map((item) => ({
    x: `${numberToChinese(item.classGrade ?? 0)}-${item.sem}`,
    y: item.height,
  }));

  const weightSeries = chartHistory.map((item) => ({
    x: `${numberToChinese(item.classGrade ?? 0)}-${item.sem}`,
    y: item.weight,
  }));

  const series = [
    {
      name: '身高',
      data: heightSeries,
    },
    {
      name: '體重',
      data: weightSeries,
    },
  ];

  const chartOptions: ApexOptions = {
    chart: {
      id: 'growth-chart',
      toolbar: { show: false },
    },
    title: {
      text: '生長發育圖',
      align: 'center',
      style: { fontSize: '18px', fontWeight: 'bold' },
    },
    xaxis: {
      type: 'category',
      title: { text: '學期年級' },
    },
    yaxis: [
      {
        seriesName: '身高',
        title: {
          text: '身高（公分）',
        },
        labels: {
          formatter: (val) => val.toFixed(1),
        },
      },
      {
        seriesName: '體重',
        opposite: true,
        title: {
          text: '體重（公斤）',
        },
        labels: {
          formatter: (val) => val.toFixed(1),
        },
      },
    ],
    legend: {
      position: 'top',
      horizontalAlign: 'center',
    },
    grid: {
      show: true,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 5,
    },
    stroke: {
      width: 3,
      curve: 'straight',
    },
  };

  const growthChart = chartReady ? (
    <Chart options={chartOptions} series={series} type="line" height={400} />
  ) : null;

  return (
    <>
      <div className="sheet-title">
        <span>身高體重</span>
        <Button
          variant="link"
          size="sm"
          className="px-2 py-1 text-decoration-none"
          onClick={async () => {
            if (whs.length === 0) {
              toast.warning('無身高體重資料，無法匯出');
              return;
            }
            setChartReady(true);
            await new Promise((resolve) => setTimeout(resolve, 800));
            await handleExportExcelWithChart();
            toast.success('生長發育歷程已匯出');
          }}
        >
          歷程
        </Button>
        <Button
          key="cell-edit"
          className="border-0 px-2 py-1"
          variant="link"
          size="sm"
          onClick={() => setInputing(true)}
        >
          <span className="feather icon-edit" />
        </Button>
      </div>

      <Table bordered className="student-phi">
        <tbody>
          <tr>
            <th>身高</th>
            <td>{wh?.height ?? ''}</td>
          </tr>
          <tr>
            <th>體重</th>
            <td>{wh?.weight ?? ''}</td>
          </tr>
          <tr>
            <th>評值</th>
            <td>{wh?.bmiCode ? I18N.BmiCode[wh?.bmiCode] : ''}</td>
          </tr>
          <tr>
            <th>實歲</th>
            <td>{student.birth && calculateAge(student.birth, wh?.examDate)}</td>
          </tr>
        </tbody>
      </Table>

      <Modal
        show={inputing}
        backdrop="static"
        dialogClassName="modal-dialog-full"
        centered
      >
        <Modal.Header closeButton onHide={() => setInputing(false)}>
          <Modal.Title>
            身高體重數據 -{' '}
            <strong>
              {student.seat} {student.name}
            </strong>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <InlineEditableTable
            values={whs}
            headers={inlineWHTableHeader}
            onValueUpdate={onValueUpdate}
            onValueDelete={onValueDelete}
            onRowRender={(v) => ({
              editable:
                editable &&
                (!v.schoolId || schoolId === v.schoolId) &&
                semesters.some((s) => s.sem === v.sem && s.year === v.year),
              deleteable:
                (!v.schoolId || schoolId === v.schoolId) &&
                semesters.some((s) => s.sem === v.sem && s.year === v.year),
            })}
          />
        </Modal.Body>
      </Modal>
      <div
        ref={chartRef}
        style={{ position: 'absolute', top: -10000, left: -10000, opacity: 0 }}
      >
        {growthChart}
      </div>
    </>
  );
};
